import { forwardRef } from "react";
import type { ReactElement, InputHTMLAttributes } from "react";

import { cn } from "@utils/tailwind.util";
import { FormLabel } from "./FormLabel";
import useSystemStore from "@store/useSystemStore";
import { APP_COLORS } from "@styles/colors";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hintText?: string;
  StartIcon?: ReactElement;
  EndIcon?: ReactElement;
  endIconClassName?: string;
  errorMessage?: string;
  containerClassName?: string;
  containerStyle?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  subContainerClassName?: string;
  isInvalid?: boolean;
  isFullWidth?: boolean;
  showErrorIcon?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = "",
      containerClassName = "",
      subContainerClassName = "",
      hintText,
      id,
      StartIcon,
      EndIcon,
      endIconClassName,
      label,
      errorMessage,
      disabled = false,
      isInvalid = false,
      isFullWidth = false,
      showErrorIcon = false,
      containerStyle,
      inputStyle,
      ...restInputProps
    },
    ref
  ) => {
    const { isDarkMode } = useSystemStore();

    const errorColor = errorMessage
      ? isDarkMode
        ? APP_COLORS.content.errorLight
        : APP_COLORS.content.error
      : isDarkMode
        ? APP_COLORS.content.warningLight
        : APP_COLORS.content.warning;

    return (
      <div
        className={cn("flex flex-col items-start font-inter", containerClassName)}
        style={containerStyle}
      >
        {label && (
          <FormLabel htmlFor={id} isInvalid={isInvalid} disabled={disabled}>
            {label}
          </FormLabel>
        )}
        <div className={cn("relative flex", { "w-full": isFullWidth }, subContainerClassName)}>
          {StartIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 transform">{StartIcon}</div>
          )}

          <input
            className={cn(
              "rounded-lg",
              "text-sm font-normal not-italic placeholder-shown:overflow-ellipsis",
              StartIcon ? "pl-10" : "pl-3",
              EndIcon ? "pr-10 py-2" : "pr-3 py-2",
              disabled
                ? "bg-background-secondary dark:bg-background-secondaryDark text-content-disabled"
                : "bg-background-primary dark:bg-background-primaryDark text-text-primary dark:text-text-primaryDark",
              errorMessage || isInvalid
                ? "border-red-600"
                : "border-stroke-primary dark:border-stroke-primaryDark",
              "hover:enabled:border-stroke-secondary dark:hover:enabled:border-stroke-secondaryDark",
              "focus:enabled:border-stroke-actionPrimary dark:focus:enabled:border-stroke-actionPrimaryDark",
              "focus:enabled:shadow-mainButtonFocus focus:enabled:ring-0",
              "active:enabled:border-stroke-actionPrimary",
              "placeholder:text-content-disabled",
              { "w-full": isFullWidth },
              className
            )}
            id={id}
            ref={ref}
            disabled={disabled}
            style={inputStyle}
            {...restInputProps}
          />

          {/* Only when there's something to show: an empty overlay would sit on top of native
              controls like the date input's calendar button and swallow the click. */}
          {EndIcon && (
            <div
              className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 transform",
                endIconClassName
              )}
            >
              {EndIcon}
            </div>
          )}
        </div>

        {(hintText || errorMessage) && (
          <span className="mt-1.5 font-normal not-italic text-[13px]" style={{ color: errorColor }}>
            {errorMessage || hintText}
          </span>
        )}
      </div>
    );
  }
);
